import { useState } from "react";
import type { Chair, CreateChair, UpdateChair } from "@dentacore/shared-types";

import { RowMenu } from "../ui/popovers";
import styles from "./settings.module.css";

/**
 * The rooms in one location — add, rename, reorder, retire, bring
 * back.
 *
 * **There is no "number of chairs" field, deliberately.** A chair is a named
 * row whose `sortOrder` is the calendar's column order, so a clinic adds rooms
 * and names them rather than setting a count — a count is exactly the kind of
 * assumption CLAUDE.md forbids. The number down the left is the calendar column
 * this chair draws as, which is what makes reordering mean something on screen.
 *
 * **Two guards on retiring, not one** (decision S1): a chair with a *history*
 * gets an "are you sure", a chair with bookings **still to come** is refused by
 * the server. So this sends the write and lets the refusal arrive rather than
 * asking about something that cannot happen.
 *
 * **Who is rostered to a chair today is not here.** That is a fact about one
 * day rather than a setting, and the Calendar's own dialog owns it.
 *
 * **Turnover is gone** (2026-10-11, decision NT1): a chair has a name and an
 * order. The calendar's *Edit chairs* dialog offers the same fields on the same
 * endpoint — two screens administering chairs must not disagree about what a
 * chair has.
 */
export function ChairsPanel({
  chairs,
  busy,
  onCreate,
  onUpdate,
  onReorder,
}: {
  chairs: readonly Chair[] | null;
  busy: boolean;
  onCreate: (body: CreateChair) => void;
  onUpdate: (chairId: string, body: UpdateChair, done: string) => void;
  onReorder: (ordered: readonly Chair[]) => void;
}): React.JSX.Element {
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState("");
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");

  /** The list with one chair moved a place, for the caller to renumber. */
  const moved = (from: number, to: number): readonly Chair[] => {
    const next = [...(chairs ?? [])];
    const [chair] = next.splice(from, 1);
    if (chair !== undefined) next.splice(to, 0, chair);
    return next;
  };

  return (
    <section className={styles.card}>
      <div className={styles.cardHead}>
        <div>
          <h2 className={styles.cardTitle}>Chairs</h2>
        </div>
        {!adding && chairs !== null && (
          <button
            type="button"
            className={styles.primary}
            disabled={busy}
            onClick={() => setAdding(true)}
            data-testid="chair-add-open"
          >
            Add a chair
          </button>
        )}
      </div>

      {adding && (
        <form
          className={styles.cardBody}
          onSubmit={(event) => {
            event.preventDefault();
            if (newName.trim() === "") return;
            onCreate({ name: newName.trim(), sortOrder: (chairs?.length ?? 0) + 1 });
            setNewName("");
            setAdding(false);
          }}
        >
          <label className={styles.field}>
            <span className={styles.label}>Name</span>
            <input
              className={styles.input}
              value={newName}
              onChange={(event) => setNewName(event.target.value)}
              placeholder="Whatever this room is called"
              autoFocus
              data-testid="new-chair-name"
            />
          </label>
          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.primary}
              disabled={busy || newName.trim() === ""}
              data-testid="new-chair-add"
            >
              Add it
            </button>
            <button
              type="button"
              className={styles.ghost}
              disabled={busy}
              onClick={() => {
                setNewName("");
                setAdding(false);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {chairs === null ? (
        <div className={styles.cardBody}>
          <p className={styles.quiet}>Reading&hellip;</p>
        </div>
      ) : chairs.length === 0 ? (
        <div className={styles.cardBody}>
          <p className={styles.blank} data-testid="no-chairs">
            No chairs yet. This location cannot take a booking until it has one.
          </p>
        </div>
      ) : (
        <ol className={styles.cardRows}>
          {chairs.map((chair, index) => (
            <li key={chair.id}>
              <div className={chair.isActive ? styles.row : styles.rowMuted}>
                <span className={styles.seat} aria-hidden="true">
                  {index + 1}
                </span>

                {renamingId === chair.id ? (
                  <form
                    className={styles.inlineForm}
                    onSubmit={(event) => {
                      event.preventDefault();
                      if (nameInput.trim() === "") return;
                      onUpdate(chair.id, { name: nameInput.trim() }, "Chair saved.");
                      setRenamingId(null);
                    }}
                  >
                    <input
                      className={styles.input}
                      value={nameInput}
                      onChange={(event) => setNameInput(event.target.value)}
                      aria-label="Chair name"
                      autoFocus
                      data-testid="chair-name-input"
                    />
                    <button
                      type="submit"
                      className={styles.primary}
                      disabled={busy || nameInput.trim() === ""}
                      data-testid="chair-rename-save"
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className={styles.ghost}
                      disabled={busy}
                      onClick={() => setRenamingId(null)}
                      data-testid="chair-rename-cancel"
                    >
                      Cancel
                    </button>
                  </form>
                ) : (
                  <>
                    <span className={styles.rowBody}>
                      <span className={styles.rowTitle}>
                        <span className={styles.rowName} data-testid="chair-name">
                          {chair.name}
                        </span>
                        {!chair.isActive && (
                          <span className={styles.tagQuiet} data-testid="chair-retired">
                            Retired
                          </span>
                        )}
                        {chair.futureAppointmentCount > 0 && (
                          <span className={styles.tagWarm} data-testid="chair-upcoming">
                            {chair.futureAppointmentCount} still to come
                          </span>
                        )}
                      </span>
                      <span className={styles.rowMeta}>
                        {chair.appointmentCount === 0
                          ? "Never used"
                          : `${chair.appointmentCount} appointment${
                              chair.appointmentCount === 1 ? "" : "s"
                            } in all`}
                      </span>
                    </span>

                    {/*
                      **One ⋯ per row** (decision CS10): Edit, Move left, Move
                      right and Retire, every one of them still here. A retired
                      chair keeps *Bring back* on the row, because that is the
                      one thing anybody opens a retired chair to do.
                    */}
                    <span className={styles.rowActions}>
                      {!chair.isActive && (
                        <button
                          type="button"
                          className={styles.ghost}
                          disabled={busy}
                          onClick={() =>
                            onUpdate(chair.id, { isActive: true }, `${chair.name} is back.`)
                          }
                          data-testid="chair-reopen"
                        >
                          Bring back
                        </button>
                      )}
                      <RowMenu
                        label={`More for ${chair.name}`}
                        testId="chair-menu"
                        items={[
                          {
                            // Edit, not Rename — Anthony QA #12.
                            label: "Edit",
                            testId: "chair-rename",
                            disabled: busy,
                            onSelect: () => {
                              setNameInput(chair.name);
                              setRenamingId(chair.id);
                            },
                          },
                          {
                            label: "Move left on the calendar",
                            testId: "chair-move-up",
                            disabled: busy || index === 0,
                            onSelect: () => onReorder(moved(index, index - 1)),
                          },
                          {
                            label: "Move right on the calendar",
                            testId: "chair-move-down",
                            disabled: busy || index === chairs.length - 1,
                            onSelect: () => onReorder(moved(index, index + 1)),
                          },
                          ...(chair.isActive
                            ? [
                                {
                                  label: "Retire",
                                  testId: "chair-retire",
                                  danger: true,
                                  disabled: busy,
                                  onSelect: () => {
                                    // The confirm is for a long history. Bookings
                                    // still to come are refused by the server
                                    // (decision S1), so this sends the write and
                                    // lets the count arrive rather than asking
                                    // about the impossible.
                                    if (
                                      chair.appointmentCount > 0 &&
                                      chair.futureAppointmentCount === 0
                                    ) {
                                      setConfirmingId(chair.id);
                                      return;
                                    }
                                    onUpdate(
                                      chair.id,
                                      { isActive: false },
                                      `${chair.name} retired.`,
                                    );
                                  },
                                },
                              ]
                            : []),
                        ]}
                      />
                    </span>
                  </>
                )}
              </div>

              {confirmingId === chair.id && (
                <div className={styles.confirm} role="alert">
                  <p className={styles.confirmText}>
                    Retire {chair.name}? It carries {chair.appointmentCount} appointment
                    {chair.appointmentCount === 1 ? "" : "s"}, all in the past. Nothing is deleted
                    &mdash; it leaves the calendar and its history stays readable.
                  </p>
                  <span className={styles.confirmActions}>
                    <button
                      type="button"
                      className={styles.danger}
                      disabled={busy}
                      onClick={() => {
                        onUpdate(chair.id, { isActive: false }, `${chair.name} retired.`);
                        setConfirmingId(null);
                      }}
                      data-testid="chair-retire-confirm"
                    >
                      Retire it
                    </button>
                    <button
                      type="button"
                      className={styles.ghost}
                      disabled={busy}
                      onClick={() => setConfirmingId(null)}
                      data-testid="chair-retire-cancel"
                    >
                      Keep it
                    </button>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
